import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Plus, Search, Settings, X, AlertTriangle, MessageSquareWarning } from 'lucide-react';
import { useQuotes } from '../hooks/useQuotes';
import { norm } from '../utils/catalog';
import { STATUS_LABELS, STATUS_TONES, autoApproved, fmtDate, formatDocNumber, money, needsReview, shownStatus } from '../utils/quotes';

const inputCls = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green';
const labelCls = 'text-[10px] uppercase font-bold text-seryi-text';
const PAGE_SIZE = 50;

export function StatusBadge({ status }) {
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_TONES[status] || STATUS_TONES.borrador}`}>{STATUS_LABELS[status] || status}</span>;
}

function SettingsPanel({ settings, nextNumbers, onSave, onClose, readOnly }) {
  const [f, setF] = useState(() => ({ ...settings, auto_approve_max_amount: settings.auto_approve_max_amount ?? '', quote_next_number: nextNumbers?.quote ?? 1, sale_next_number: nextNumbers?.sale ?? 1 }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const text = (k, label, props = {}) => (
    <div className="space-y-1">
      <label htmlFor={`bs-${k}`} className={labelCls}>{label}</label>
      <input id={`bs-${k}`} className={inputCls} value={f[k] ?? ''} onChange={(e) => set(k, e.target.value)} {...props} />
    </div>
  );
  return (
    <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-3xl space-y-4 shadow-xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-sm text-seryi-heading">Ajustes de cotizaciones</h3>
          <p className="text-[11px] text-seryi-text mt-1">Lo que aparece en el encabezado del documento y cómo se numeran las cotizaciones.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Cerrar ajustes" className="text-seryi-text hover:text-seryi-heading"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {text('legal_name', 'Nombre o razón social')}
        {text('tax_id', 'NIT o cédula')}
        {text('address', 'Dirección')}
        {text('phone', 'Teléfono')}
        {text('email', 'Correo')}
        {text('quote_valid_days', 'Vigencia por defecto (días)', { type: 'number', min: 1, max: 365 })}
        <div className="space-y-1">
          <label htmlFor="bs-currency" className={labelCls}>Moneda</label>
          <select id="bs-currency" className={inputCls} value={f.currency} onChange={(e) => set('currency', e.target.value)}>
            <option value="COP">COP (pesos colombianos)</option>
            <option value="USD">USD (dólares)</option>
          </select>
        </div>
      </div>
      <div className="p-3 rounded-xl border border-seryi-bg-light space-y-3">
        <div>
          <p className="text-xs font-semibold text-seryi-heading">Numeración de los documentos</p>
          <p className="text-[10px] text-seryi-text">El número sale como PREFIJO-0001 o PREFIJO-0001-SUFIJO. Cambiar el prefijo, el sufijo o el número inicial solo afecta a los documentos nuevos; los que ya existen conservan su número. Las ventas son el documento interno de cobro: no son la factura electrónica, que la emite tu proveedor de facturación.</p>
        </div>
        {[['quote', 'Cotizaciones', 'Prefijo del número (ej. COT)'], ['sale', 'Ventas (facturas internas)', 'Prefijo de las ventas (ej. VTA)']].map(([kind, title, prefixLabel]) => (
          <div key={kind} className="space-y-1">
            <p className={labelCls}>{title}</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              {text(`${kind}_prefix`, prefixLabel, { maxLength: 8 })}
              {text(`${kind}_suffix`, 'Sufijo (opcional, ej. 2026)', { maxLength: 8 })}
              <div className="space-y-1">
                <label htmlFor={`bs-${kind}-next`} className={labelCls}>Próximo número ({title.toLowerCase()})</label>
                <input id={`bs-${kind}-next`} type="number" min="1" max="99999999" step="1" className={inputCls} value={f[`${kind}_next_number`]} onChange={(e) => set(`${kind}_next_number`, e.target.value)} />
              </div>
              <p className="text-[11px] text-seryi-text pb-2">Quedará: <span className="font-mono font-bold text-seryi-heading">{formatDocNumber(String(f[`${kind}_prefix`] || '').toUpperCase(), String(f[`${kind}_suffix`] || '').toUpperCase(), f[`${kind}_next_number`])}</span></p>
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-1">
        <label htmlFor="bs-footer" className={labelCls}>Texto al pie del documento (condiciones, cuentas para pagar, etc.)</label>
        <textarea id="bs-footer" rows={3} className={inputCls} value={f.footer_text ?? ''} onChange={(e) => set('footer_text', e.target.value)} />
      </div>
      <div className="space-y-1">
        <label htmlFor="bs-stock" className={labelCls}>Si no alcanzan las existencias al vender</label>
        <select id="bs-stock" className={inputCls} value={f.stock_policy} onChange={(e) => set('stock_policy', e.target.value)}>
          <option value="warn">Avisar y dejar vender</option>
          <option value="block">No dejar vender</option>
          <option value="allow_negative">Dejar vender sin avisar</option>
        </select>
        <p className="text-[10px] text-seryi-text">Se aplica cuando las ventas descuenten existencias; al cotizar solo se avisa.</p>
      </div>
      <div className="p-3 rounded-xl border border-seryi-bg-light space-y-2">
        <label className="flex items-center gap-2 text-xs text-seryi-heading font-semibold">
          <input type="checkbox" checked={Boolean(f.auto_approve)} onChange={(e) => set('auto_approve', e.target.checked)} />
          Dejar que el bot apruebe solo las cotizaciones pequeñas
        </label>
        <div className="space-y-1 max-w-xs">
          <label htmlFor="bs-cap" className={labelCls}>Tope por cotización ({f.currency})</label>
          <input id="bs-cap" type="number" min="0" step="any" className={inputCls} value={f.auto_approve_max_amount} onChange={(e) => set('auto_approve_max_amount', e.target.value)} />
        </div>
        <p className="text-[10px] text-seryi-text">Sin tope no se aprueba nada solo. Solo cotizaciones del bot iguales o menores al tope; las que armas tú las apruebas tú.</p>
      </div>
      <div className="p-3 rounded-xl border border-seryi-bg-light space-y-2">
        <label className="flex items-center gap-2 text-xs text-seryi-heading font-semibold">
          <input type="checkbox" checked={Boolean(f.auto_send_on_approval)} onChange={(e) => set('auto_send_on_approval', e.target.checked)} />
          Enviar sola por WhatsApp la cotización cuando se apruebe
        </label>
        <p className="text-[10px] text-seryi-text">Al aprobar, el sistema le escribe al cliente (con el número de su ficha) un resumen y el enlace para ver y descargar la cotización, y la pasa a «enviada». Si el cliente no tiene número, queda aprobada y avisa. Siempre puedes enviarla tú a mano desde la cotización.</p>
      </div>
      <div className="p-3 rounded-xl border border-seryi-bg-light space-y-2">
        <div className="space-y-1 max-w-xs">
          <label htmlFor="bs-notify" className={labelCls}>WhatsApp para avisar de cambios pedidos</label>
          <input id="bs-notify" inputMode="tel" placeholder="Ej. 573016867636" className={inputCls} value={f.notify_phone ?? ''} onChange={(e) => set('notify_phone', e.target.value)} />
        </div>
        <p className="text-[10px] text-seryi-text">Cuando un cliente le pide al bot cambiar una cotización que ya recibió, el bot lo anota, la cotización queda marcada «Pidió cambio» y se le escribe a este número (el tuyo o el de tu equipo, con indicativo del país). Déjalo vacío para no recibir aviso por WhatsApp: la marca en la app siempre aparece.</p>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="text-xs font-bold text-seryi-text px-3 py-2">Cancelar</button>
        <button type="button" disabled={saving || readOnly} onClick={async () => { setSaving(true); const ok = await onSave(f); setSaving(false); if (ok) onClose(); }}
          className="bg-seryi-green text-seryi-bg-dark font-extrabold text-xs px-4 py-2 rounded-xl disabled:opacity-50">{saving ? 'Guardando…' : 'Guardar ajustes'}</button>
      </div>
    </div>
  );
}

const FILTERS = [['activas', 'Todas (sin reemplazadas)'], ['revisar', 'Por revisar (del bot)'], ['cambio', 'Con cambio pedido'], ['borrador', 'Borrador'], ['aprobada', 'Aprobada'], ['enviada', 'Enviada'],
  ['aceptada', 'Aceptada'], ['rechazada', 'Rechazada'], ['vencida', 'Vencida'], ['reemplazada', 'Reemplazada']];

export default function QuotesPage() {
  const { quotes, customers, settings, loading, tableMissing, isReadOnlyDemo, saveSettings, openChanges, nextNumbers } = useQuotes();
  const [filter, setFilter] = useState('activas');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [showSettings, setShowSettings] = useState(false);

  const customerById = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers]);
  const toReview = quotes.filter(needsReview).length;
  const toSend = quotes.filter((q) => q.status === 'aprobada').length;
  const withChange = quotes.filter((q) => openChanges[q.id]).length;

  const filtered = useMemo(() => quotes.filter((q) => {
    const shown = shownStatus(q);
    if (filter === 'activas' && q.status === 'reemplazada') return false;
    if (filter === 'revisar' && !needsReview(q)) return false;
    if (filter === 'cambio' && !openChanges[q.id]) return false;
    if (!['activas', 'revisar', 'cambio'].includes(filter) && shown !== filter) return false;
    const terms = norm(query).split(' ').filter(Boolean);
    if (!terms.length) return true;
    const c = customerById.get(q.customer_id);
    const hay = norm(`${q.number} ${c?.name ?? ''} ${c?.legal_name ?? ''} ${c?.phone ?? ''}`);
    return terms.every((t) => hay.includes(t));
  }), [quotes, filter, query, customerById, openChanges]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const visible = filtered.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  if (tableMissing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261003b_billing_base.sql</code> en Supabase para usar las cotizaciones.</div>;
  }

  return (
    <div className="space-y-5 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><FileText size={20} /> Cotizaciones</h2>
          <p className="text-xs text-seryi-text">Arma, aprueba y envía cotizaciones con los precios de tu catálogo. Una cotización no es una factura electrónica.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setShowSettings(!showSettings)} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"><Settings size={13} /> Ajustes</button>
          <Link to="/v2/cotizaciones/nueva" className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-3 py-2 rounded-xl flex items-center gap-1.5"><Plus size={13} /> Nueva cotización</Link>
        </div>
      </div>

      {showSettings && <SettingsPanel settings={settings} nextNumbers={nextNumbers} onSave={saveSettings} onClose={() => setShowSettings(false)} readOnly={isReadOnlyDemo} />}

      {toReview > 0 && (
        <div className="p-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200 flex items-center gap-2">
          <AlertTriangle size={14} /> {toReview === 1 ? '1 cotización armada por el bot espera' : `${toReview} cotizaciones armadas por el bot esperan`} tu revisión.
          <button type="button" className="underline font-bold" onClick={() => { setFilter('revisar'); setPage(0); }}>Verlas</button>
        </div>
      )}

      {withChange > 0 && (
        <div className="p-3 rounded-xl border border-rose-500/40 bg-rose-500/10 text-xs text-rose-200 flex items-center gap-2">
          <MessageSquareWarning size={14} /> {withChange === 1 ? '1 cliente pidió' : `${withChange} clientes pidieron`} un cambio en su cotización.
          <button type="button" className="underline font-bold" onClick={() => { setFilter('cambio'); setPage(0); }}>Verlas</button>
        </div>
      )}

      {toSend > 0 && (
        <div className="p-3 rounded-xl border border-sky-500/40 bg-sky-500/10 text-xs text-sky-200 flex items-center gap-2">
          {toSend === 1 ? '1 cotización aprobada espera' : `${toSend} cotizaciones aprobadas esperan`} que la envíes al cliente.
          <button type="button" className="underline font-bold" onClick={() => { setFilter('aprobada'); setPage(0); }}>Ver aprobadas</button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" />
          <input className={`${inputCls} pl-8`} placeholder="Buscar por número o cliente" aria-label="Buscar cotizaciones" value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} />
        </div>
        <select aria-label="Filtrar por estado" className={`${inputCls} w-auto`} value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }}>
          {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span className="text-[11px] text-seryi-text">{filtered.length} {filtered.length === 1 ? 'cotización' : 'cotizaciones'}</span>
      </div>

      {loading ? (
        <p className="text-xs text-seryi-text">Cargando…</p>
      ) : quotes.length === 0 ? (
        <div className="p-8 text-center text-xs text-seryi-text border border-dashed border-seryi-bg-light rounded-3xl">
          Aún no hay cotizaciones. Crea la primera con "Nueva cotización" (necesitas productos en el Catálogo).
        </div>
      ) : (
        <div className="overflow-auto rounded-3xl border border-seryi-bg-light">
          <table className="w-full text-xs text-seryi-heading">
            <thead className="bg-seryi-bg-dark text-seryi-text text-[10px] uppercase">
              <tr><th className="p-3 text-left">Número</th><th className="p-3 text-left">Cliente</th><th className="p-3 text-left">Estado</th><th className="p-3 text-left">Creada</th><th className="p-3 text-left">Vigente hasta</th><th className="p-3 text-right">Total</th></tr>
            </thead>
            <tbody>
              {visible.map((q) => {
                const c = customerById.get(q.customer_id);
                return (
                  <tr key={q.id} className="border-t border-seryi-bg-light hover:bg-seryi-bg-light/20">
                    <td className="p-3 font-mono font-bold"><Link to={`/v2/cotizaciones/${q.id}`} className="text-seryi-green hover:underline">{q.number}{q.version > 1 ? ` v${q.version}` : ''}</Link></td>
                    <td className="p-3">{c?.legal_name || c?.name || <span className="text-seryi-text">Sin cliente</span>}</td>
                    <td className="p-3"><StatusBadge status={shownStatus(q)} />{needsReview(q) && <span className="ml-1.5 text-[10px] font-bold text-amber-300">Por revisar · bot</span>}{autoApproved(q) && <span className="ml-1.5 text-[10px] font-bold text-sky-300">Aprobada sola · bot</span>}{openChanges[q.id] && <span className="ml-1.5 text-[10px] font-bold text-rose-300">Pidió cambio</span>}</td>
                    <td className="p-3">{fmtDate(q.created_at)}</td>
                    <td className="p-3">{fmtDate(q.valid_until)}</td>
                    <td className="p-3 text-right font-bold text-emerald-400">{money(q.total)}</td>
                  </tr>
                );
              })}
              {visible.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-seryi-text">Ninguna cotización coincide con el filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-xs text-seryi-text">
          <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)} className="px-3 py-1 border border-seryi-bg-light rounded-lg disabled:opacity-40">Anterior</button>
          <span>Página {current + 1} de {pages}</span>
          <button type="button" disabled={current >= pages - 1} onClick={() => setPage(current + 1)} className="px-3 py-1 border border-seryi-bg-light rounded-lg disabled:opacity-40">Siguiente</button>
        </div>
      )}
    </div>
  );
}
